import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { useAppActivity } from "../../../Contexts/AppActivityContext";
import { SOUND_NAME } from "../../../sounds";
import { PixiSettings } from "../../../pixiSettings";
import Overlay from "../../../Games/Shared/Overlay/Overlay";
import WinAnimationController from "./WinAnimationController";
import TextButton from "../../UserInterface/primary/Buttons/TextButton";
import {
  selectWinAnimationsPlaying,
  selectBet,
  selectCurrentRound,
  selectCurrentFreeSpinRound,
  selectFreeSpinsActive,
} from "../../../State/selectors";
import { soundManager } from "../../../SoundManager";

const WinAnimation = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const winControllerRef = useRef(null);
  const winContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const clickIndicatorRef = useRef(null);
  const buttonRef = useRef(null);
  const dimensionsContext = useDimensions();
  const {
    dimensions,
    uiSymbolHeight: uiSymbolSize,
    device,
  } = dimensionsContext;
  const { isActive, resetSpinTimer } = useAppActivity();

  // Determine button text based on device type
  const continueButtonKey = useMemo(() => {
    return device === PixiSettings.DEVICE.DESKTOP
      ? "click_to_continue"
      : "tap_to_continue";
  }, [device]);

  // Get win animations playing state and actual game data
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const bet = useSelector(selectBet);
  const currentRound = useSelector(selectCurrentRound);
  const currentFreeSpinRound = useSelector(selectCurrentFreeSpinRound);
  const freeSpinsActive = useSelector(selectFreeSpinsActive);

  // Use actual win amount from current round OR free spin round
  const winAmount = freeSpinsActive
    ? currentFreeSpinRound?.win || 0
    : currentRound?.win || 0;

  // Initialize WinAnimationController when assets are ready
  useEffect(() => {
    if (winContainerRef.current && !winControllerRef.current) {
      // Set button alpha to 0 for this instance
      if (buttonRef.current) {
        buttonRef.current.alpha = 0;
      }

      winControllerRef.current = new WinAnimationController(
        winContainerRef.current,
        dimensionsContext,
        overlayRef,
        dispatch,
        uiSymbolSize,
        uiSymbolSize,
        clickIndicatorRef,
        buttonRef,
        t
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (winControllerRef.current && uiSymbolSize && dimensionsContext) {
      winControllerRef.current.updateDimensions(
        dimensionsContext,
        uiSymbolSize,
        uiSymbolSize
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiSymbolSize]);

  // Handle user click to continue animation
  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      if (winControllerRef.current && winAnimationsPlaying) {
        const accepted = winControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [winAnimationsPlaying]
  );

  // Add spacebar handling - ensure this takes priority over other handlers
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle spacebar
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat (holding the key)
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      // Only handle when animations are playing AND controller exists
      if (!winAnimationsPlaying || !winControllerRef.current) {
        return;
      }

      // Prevent event from reaching other handlers
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Emergency recovery check
      try {
        const accepted = winControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      } catch (error) {
        // Emergency recovery - try to finish animation
        if (
          winControllerRef.current &&
          winControllerRef.current.finishAnimation
        ) {
          winControllerRef.current.finishAnimation();
        }
      }
    };

    // Use capture phase to ensure we get the event first
    if (winAnimationsPlaying) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false, // Allow preventDefault
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [winAnimationsPlaying, dispatch]);

  // Handle app activity changes - pause/resume spine animations
  useEffect(() => {
    if (winControllerRef.current) {
      winControllerRef.current.handleActivityChange(isActive);
    }
  }, [isActive]);

  // Start animation when winAnimationsPlaying becomes true and we have a valid win
  useEffect(() => {
    if (winAnimationsPlaying && winControllerRef.current && winAmount > 0) {
      // Reset idle timer when win animations start
      resetSpinTimer();
      // Start progressive animation sequence with actual win data
      winControllerRef.current.startSequence(winAmount, bet);
    } else if (!winAnimationsPlaying && winControllerRef.current) {
      // Stop animation when state becomes false
      winControllerRef.current.stopWinAnimation();
    }
  }, [
    winAnimationsPlaying,
    winAmount,
    bet,
    freeSpinsActive,
    resetSpinTimer,
    currentRound,
    currentFreeSpinRound,
  ]);

  return (
    <Container
      name="win_animation"
      x={dimensions.width / 2}
      y={dimensions.height / 2}
      sortableChildren={true}
      zIndex={10}
      ref={winContainerRef}
    >
      {/* Background overlay - clickable to continue */}
      <Overlay
        ref={overlayRef}
        dimensions={dimensions}
        interactive={winAnimationsPlaying}
        onPointerDown={handleBackgroundClick}
        x={-dimensions.width / 2}
        y={-dimensions.height / 2}
        fadeDuration={350}
      />

      {/* Win spine and counter are managed internally by WinAnimationController */}

      <TextButton
        ref={buttonRef}
        y={dimensions.height / 2}
        translationKey={continueButtonKey}
      />
    </Container>
  );
};

export default WinAnimation;
